import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import {
    cancelCreditNote,
    deleteCreditNote,
    generateEcreditNote,
    getCreditNoteDetails,
    getCreditNoteEinvoiceConfig,
} from '../../services/CreditNoteServices';
import { fetchAndShowCreditNotePdf } from '../../utils/creditNotePdf';
import InvoiceEinvoiceLockBanner from '../../components/InvoiceEinvoiceLockBanner';
import InvoiceEinvoiceSection from '../../components/InvoiceEinvoiceSection';
import {
    buildCreditNoteTotals,
    getCreditNoteTypeLabel,
} from './CreditNoteUtils';
import TableRowsCreditNote from './TableRowsCreditNote';
import moment from 'moment';

function CreditNoteEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [ecreditConfig, setEcreditConfig] = useState(null);
    const [ecreditResult, setEcreditResult] = useState(null);
    const [isFetchingIrn, setIsFetchingIrn] = useState(false);
    const isCancelled = inputs.status === false || inputs.status === 0;

    const totals = useMemo(
        () => buildCreditNoteTotals(productLines, invoiceLines),
        [productLines, invoiceLines],
    );

    useEffect(() => {
        setInProgress(true);
        getCreditNoteDetails(cookies, id)
            .then((response) => {
                setInputs({
                    ...response,
                    cn_date: moment(response.cn_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                    cn_no: response.cn_no,
                    status: response.status,
                });
                setProductLines(response.product_lines || []);
                setInvoiceLines(response.invoice_lines || []);
                if (response.einvoice_irn) {
                    setEcreditResult({
                        einvoice_irn: response.einvoice_irn,
                        einvoice_ack: response.einvoice_ack,
                    });
                }
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load credit note.' }, 'Load Failed');
            })
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.cn_date || !inputs.invoice_id) {
            setEcreditConfig(null);
            return;
        }
        getCreditNoteEinvoiceConfig(
            cookies,
            inputs.plant_id,
            moment(inputs.cn_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
            totals.credit_amt || inputs.credit_amt || 0,
            inputs.invoice_id,
        ).then((response) => setEcreditConfig({
            ...response,
            einvoice_irn: ecreditResult?.einvoice_irn || inputs.einvoice_irn,
            einvoice_ack: ecreditResult?.einvoice_ack || inputs.einvoice_ack,
        })).catch(() => setEcreditConfig(null));
    }, [cookies, inputs.plant_id, inputs.cn_date, inputs.invoice_id, inputs.credit_amt, inputs.einvoice_irn, inputs.einvoice_ack, totals.credit_amt, ecreditResult]);

    const onPrintCreditNote = () => {
        setInProgress(true);
        fetchAndShowCreditNotePdf(cookies, id, { title: 'Credit Note PDF' })
            .finally(() => setInProgress(false));
    };

    const onGenerateEcreditNote = () => {
        setIsFetchingIrn(true);
        generateEcreditNote(cookies, id)
            .then((response) => {
                setEcreditResult(response);
                setInputs((values) => ({
                    ...values,
                    einvoice_irn: response.einvoice_irn || '',
                    einvoice_ack: response.einvoice_ack || '',
                    einvoice_status: response.einvoice_status,
                    einvoice_status_label: 'Generated',
                    credit_note_lock_mode: 'locked',
                    is_credit_note_editable: false,
                }));
                Swal.fire(response.message || `E-Credit Note IRN saved: ${response.einvoice_irn}`, '', 'success');
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: error?.message }, 'Generate IRN Failed');
            })
            .finally(() => setIsFetchingIrn(false));
    };

    const onCancelCreditNote = () => {
        const hasIrn = Boolean(inputs.einvoice_irn || ecreditResult?.einvoice_irn);
        const cancelHtml = hasIrn
            ? `<p>This credit note has an E-Credit Note IRN. Per GST rules, the IRN will be <b>cancelled on the GST portal</b> (within 24 hours of IRN generation), then this credit note is marked <b>inactive</b>.</p>
               <label for="swal-cancel-reason" style="display:block;margin-top:12px;font-weight:600;">GST cancel reason</label>
               <select id="swal-cancel-reason" class="form-control" style="margin-bottom:8px;">
                 <option value="2">Data entry mistake</option>
                 <option value="1">Duplicate</option>
                 <option value="3">Order cancelled</option>
                 <option value="4">Others</option>
               </select>
               <input id="swal-cancel-remarks" class="form-control" placeholder="Remarks (optional, max 100 chars)" maxlength="100" />`
            : '<p>Mark this credit note as <b>cancelled (inactive)</b>? Values are retained for audit.</p>';

        Swal.fire({
            title: hasIrn ? 'Cancel E-Credit Note IRN?' : 'Cancel this credit note?',
            html: cancelHtml,
            icon: hasIrn ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: hasIrn ? 'Cancel IRN & Credit Note' : 'Yes, cancel',
            cancelButtonText: 'No',
            focusCancel: true,
            preConfirm: () => {
                if (!hasIrn) {
                    return {
                        cancel_ecredit_note: false,
                        cancel_reason: '2',
                        cancel_remarks: 'Credit note cancelled',
                    };
                }
                return {
                    cancel_ecredit_note: true,
                    cancel_reason: document.getElementById('swal-cancel-reason')?.value || '2',
                    cancel_remarks: document.getElementById('swal-cancel-remarks')?.value || 'Credit note cancelled',
                };
            },
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelCreditNote(cookies, id, result.value || {})
                .then((response) => {
                    Swal.fire(response.message || 'Cancelled!', '', 'success');
                    navigate('/CreditNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Cancel Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const onDeleteCreditNote = () => {
        Swal.fire({
            title: 'Delete this cancelled credit note?',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deleteCreditNote(cookies, id)
                .then(() => {
                    Swal.fire('Deleted!', '', 'success');
                    navigate('/CreditNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Delete Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form>
                    <FloatingControls tableLink="/CreditNoteTable" formLink="/CreditNote" addNewLabel="Add New" enableCancel={false} />
                    <FormPageLayout
                        moduleLabel="ACCOUNTS"
                        title="Update Credit Note"
                        subtitle="Review credit note details for sales return, rate difference, discount, or other adjustments."
                        modeLabel={`${getCreditNoteTypeLabel(inputs.cn_type).toUpperCase()} UPDATE MODE`}
                        headerExtra={(
                            <InvoiceEinvoiceLockBanner
                                mode={isCancelled ? 'cancelled' : inputs.credit_note_lock_mode}
                                message={isCancelled
                                    ? 'This credit note is inactive. Values are retained for audit only.'
                                    : inputs.credit_note_lock_message}
                            />
                        )}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-5 text-right">Plant Location</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.plant_name || inputs.plant_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Customer</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.customer_name || inputs.customer_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Site</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.site_name || inputs.consignee_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Invoice</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.invoice_no || inputs.invoice_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Credit Note Type</label>
                                        <input className="form-control col-sm-6" readOnly value={getCreditNoteTypeLabel(inputs.cn_type)} /><br />
                                        <label className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.voucher_mode || ''} />
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Credit Note No.</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.cn_no || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Credit Note Date</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.cn_date || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">E-Credit Note IRN</label>
                                        <input className="form-control col-sm-6" readOnly value={ecreditResult?.einvoice_irn || inputs.einvoice_irn || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Ack No.</label>
                                        <input className="form-control col-sm-6" readOnly value={ecreditResult?.einvoice_ack || inputs.einvoice_ack || ''} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsCreditNote rowsData={productLines} taxes={[]} cnType={inputs.cn_type} readOnly />
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Plant Name</th>
                                            <th>Project/Site Name</th>
                                            <th>Invoice Number</th>
                                            <th>Invoice Date</th>
                                            <th>Invoice Amount</th>
                                            <th>Received Amount</th>
                                            <th>Balance Amount</th>
                                            <th>Credit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceLines.map((line, index) => (
                                            <tr key={`cn-edit-inv-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'Invoice'}</td>
                                                <td>{line.plant_name}</td>
                                                <td>{line.site_name}</td>
                                                <td>{line.invoice_no}</td>
                                                <td>{line.invoice_date}</td>
                                                <td>{line.invoice_amt}</td>
                                                <td>{line.received_amt}</td>
                                                <td>{line.balance_amt}</td>
                                                <td>{line.credit_amt}</td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" className="form-control col-sm-10" readOnly={isCancelled} value={inputs.remarks || ''} />
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-3 text-right">Total Credit Note Value</label>
                                <input className="form-control col-sm-3" readOnly value={totals.credit_amt || inputs.credit_amt || 0} />
                            </div>

                            <InvoiceEinvoiceSection
                                config={ecreditConfig}
                                einvoiceResult={ecreditResult}
                                onGenerateEinvoice={!isCancelled ? onGenerateEcreditNote : undefined}
                                isFetchingIrn={isFetchingIrn}
                            />
                        </div>

                        <div className="footer text-center">
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={onPrintCreditNote}>Print</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/CreditNoteTable')}>View</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>Home</Button>
                            {!isCancelled && (
                                <Button type="button" className="btn btn-cancel" style={{ width: '120px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onCancelCreditNote}>Cancel CN</Button>
                            )}
                            {isCancelled && (
                                <Button type="button" className="btn btn-delete" style={{ width: '80px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onDeleteCreditNote}>Delete</Button>
                            )}
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default CreditNoteEdit;
